import { ReactNode, useMemo } from 'react';
import {
  Asset,
  EmptyFilesUpload,
  EmptyFilesUploadError,
  FacialAuthenticationStatus,
  TrackingFamily,
} from '@facephi/inphinite-common';
import { Icon, Label } from '@facephi/ui-react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { ImageLoad } from './ImageLoad';
import { FacialValidationState, infoFacial, tInfo } from './model';
import { OperationsCardIcon } from './operationsCards/Styles';
import {
  FacialCheckGrid,
  FacialContainer,
  FacialResultIconWrapper,
  InfoContainer,
} from './Styles';
import {
  CUSTOMER_PROVIDED_IMAGE,
  DOCUMENT_FACIAL_CAPTURE,
  INTERNAL_PROVIDERS,
  SELFIE,
  NFC_PHOTO,
} from '../../state/constants';

type Props = {
  state?: FacialValidationState;
  assets?: Asset[];
  loading?: boolean;
  family?: string;
};

const ImageSkeleton = () => (
  <div>
    <Skeleton width={120} height={120} borderRadius={8} />
    <div style={{ marginTop: '0.8rem' }}>
      <Skeleton width="50%" height={18} borderRadius={8} />
    </div>
  </div>
);

export const SelfieValidations = ({
  state,
  assets = [],
  loading,
  family,
}: Props) => {
  const { t } = useTranslation();
  const [{ authStatus, source, id }] = state || [{}];

  const firstImage = useMemo(
    () =>
      assets.length
        ? assets.find(({ type }) =>
            source === INTERNAL_PROVIDERS.nfcMobile
              ? type === NFC_PHOTO
              : type === DOCUMENT_FACIAL_CAPTURE ||
                type === CUSTOMER_PROVIDED_IMAGE
          )
        : undefined,
    [assets, source]
  );

  const secondImage = useMemo(
    () =>
      assets.length ? assets.find(({ type }) => type === SELFIE) : undefined,
    [assets]
  );

  const getLoadingImages = () => (
    <>
      <ImageSkeleton />
      <Skeleton width={40} height={40} circle />
      <ImageSkeleton />
    </>
  );

  const info: tInfo = useMemo(() => {
    if (!authStatus) return infoFacial['notAble'];
    const type =
      authStatus === FacialAuthenticationStatus.positive
        ? 'match'
        : authStatus === FacialAuthenticationStatus.negative
        ? 'notMatch'
        : 'notAble';
    return infoFacial[type];
  }, [state]);

  const isInternalProvider =
    source && Object.values(INTERNAL_PROVIDERS).includes(source);

  const getEmptyImageItem = (isSecondImage?: boolean): ReactNode =>
    !isInternalProvider && isSecondImage ? (
      <OperationsCardIcon $bgColor="grey100" $remSize="8.9rem">
        <Icon
          iconName={'CameraSlash'}
          size="32"
          color="white"
          weight={'fill'}
        />
      </OperationsCardIcon>
    ) : authStatus === FacialAuthenticationStatus.positive ? (
      <EmptyFilesUpload />
    ) : (
      <EmptyFilesUploadError />
    );

  const getImages = () => (
    <>
      <FacialContainer flexDirection="column" rowGap="0.8">
        {secondImage ? (
          <ImageLoad asset={secondImage} />
        ) : (
          getEmptyImageItem(!secondImage)
        )}
      </FacialContainer>

      {info && (
        <OperationsCardIcon
          $bgColor={info.backgroundColor}
          $remSize="6rem"
          key={`provider-${id}`}
          className="facial-match-animated"
          $borderRadius="1.7rem"
        >
          <Icon
            iconName={info.iconName}
            size={info.size}
            color={info?.iconColor}
            weight={info.weight}
          />
        </OperationsCardIcon>
      )}
      <FacialContainer
        flexDirection="column"
        rowGap="0.8"
        className="facial-match-animated"
        key={`image-${firstImage?.type}`}
      >
        {family !== TrackingFamily.AUTHENTICATION && firstImage ? (
          <ImageLoad asset={firstImage} />
        ) : (
          getEmptyImageItem()
        )}
      </FacialContainer>
    </>
  );

  return (
    <>
      <FacialCheckGrid
        alignItems="center"
        columnGap="0.8"
        justifyContent="space-between"
        gridTemplateColumns="10rem auto 10rem"
        gridTemplateRows="10rem auto"
        rowGap="0.8"
      >
        {loading ? getLoadingImages() : getImages()}
        {!loading && (
          <>
            <Label className="asset-text" fontSize="12">
              {t('SELFIE')}
            </Label>
            {family !== TrackingFamily.AUTHENTICATION && (
              <Label
                fontSize="12"
                className="asset-text facial-match-animated"
                id="document-text"
                key={`label-${firstImage?.type}`}
              >
                {t(firstImage?.type || '')}
              </Label>
            )}
          </>
        )}
      </FacialCheckGrid>
      {loading ? (
        <div style={{ marginTop: '2rem', textAlign: 'center' }}>
          <Skeleton width="80%" height={18} borderRadius={8} />
        </div>
      ) : (
        <InfoContainer
          $infoBackground={info?.backgroundColor}
          $infoColor={info?.iconColor}
          rowGap="0.8"
          flexDirection="row"
          alignItems="center"
          key={`provider-${id}`}
        >
          <FacialResultIconWrapper>
            <Icon
              iconName="ChatsCircle"
              color={info?.iconColor}
              size="32"
              weight="thin"
            />
          </FacialResultIconWrapper>
          <Label fontSize="12" semibold>
            {family === TrackingFamily.AUTHENTICATION
              ? t(info?.message_authentication as string)
              : t(info?.message)}
          </Label>
        </InfoContainer>
      )}
    </>
  );
};
